/**
 * Executes an array of tasks in parallel, with optional batching (otherwise defaults to 5) and cancellation support.
 * @param tasks An array of functions that return promises.
 * @param options Options for controlling the execution.
 * @returns An array of results from the tasks.
 */
export async function batchPromiseAll<T>(
  tasks: (() => Promise<T>)[],
  options: { batchSize?: number; signal?: AbortSignal },
): Promise<T[]> {
  const results: T[] = [];
  let i = 0;

  const batchSize = options.batchSize ?? 5;
  while (i < tasks.length) {
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const batch = tasks.slice(i, i + batchSize).map(fn => fn());
    results.push(...(await Promise.all(batch)));
    i += batchSize;
  }
  return results;
}

export type RetryWithBackoffContext = {
  /**
   * 1-based attempt number for the current execution.
   */
  attempt: number;
  /**
   * Milliseconds elapsed since the first attempt started.
   */
  elapsedMs: number;
};

export type RetryWithBackoffErrorContext = RetryWithBackoffContext & {
  /**
   * Milliseconds to wait before the next retry attempt.
   */
  nextDelayMs: number;
};

export type RetryWithBackoffOptions = {
  /**
   * Maximum total time window, in milliseconds, during which retries are allowed.
   * Once the next delay would exceed this budget, the latest error is rethrown.
   */
  timeoutMs: number;
  /**
   * Base delay, in milliseconds, used by the exponential backoff formula.
   * The effective delay is `2^attemptIndex * delayMultiplierMs`.
   * @default 5000
   */
  delayMultiplierMs?: number;
  /**
   * Optional predicate that decides whether the operation should be retried after
   * a failure. Returning `false` rethrows the current error immediately.
   */
  shouldRetry?: (
    error: unknown,
    context: RetryWithBackoffErrorContext,
  ) => boolean | Promise<boolean>;
  /**
   * Optional side-effect hook invoked before waiting for the next retry.
   * Typical uses include logging, metrics, or tracing.
   */
  onRetry?: (
    error: unknown,
    context: RetryWithBackoffErrorContext,
  ) => void | Promise<void>;
};

/**
 * Calculates an exponential backoff delay using the formula `2^attempt * multiplier`.
 *
 * The first retry attempt should typically pass `attempt = 0`, producing a delay equal
 * to `multiplier`. The second retry attempt produces `2 * multiplier`, then
 * `4 * multiplier`, and so on.
 *
 * @param attempt Zero-based retry attempt index.
 * @param multiplier Base delay in milliseconds.
 * @returns The computed delay in milliseconds.
 */
export function exponentialBackoffDelay(
  attempt: number,
  multiplier: number = 5000,
): number {
  return Math.pow(2, attempt) * multiplier;
}

/**
 * Repeatedly executes an asynchronous operation using exponential backoff until it
 * succeeds or the configured time budget is exhausted.
 *
 * This helper is generic and intentionally transport-agnostic. It can be reused for
 * HTTP clients, SDK calls, database operations, or any promise-returning function that
 * may fail transiently.
 *
 * @typeParam T The resolved value type returned by the operation.
 * @param operation The promise-returning operation to execute.
 * @param options Retry configuration controlling timeout, backoff, and hooks.
 * @returns The successful result of the operation.
 * @throws Rethrows the last observed error if the timeout budget is exceeded or if
 * `shouldRetry` explicitly returns `false`.
 *
 * The computed exponential delay is capped to the remaining timeout budget so the
 * helper can continue retrying up to the configured deadline instead of stopping
 * early when the next raw delay would overshoot it.
 *
 * @example
 * await retryWithBackoff(
 *   () => client.Projects.create(payload),
 *   {
 *     timeoutMs: 30_000,
 *     delayMultiplierMs: 1_000,
 *   },
 * );
 */
export async function retryWithBackoff<T>(
  operation: (context: RetryWithBackoffContext) => Promise<T>,
  options: RetryWithBackoffOptions,
): Promise<T> {
  const startedAt = Date.now();
  let attempt = 0;
  let lastError: unknown;

  while (Date.now() - startedAt <= options.timeoutMs) {
    attempt += 1;

    try {
      return await operation({
        attempt,
        elapsedMs: Date.now() - startedAt,
      });
    } catch (error) {
      lastError = error;

      const elapsedMs = Date.now() - startedAt;
      const remainingMs = Math.max(options.timeoutMs - elapsedMs, 0);
      const nextDelayMs = Math.min(
        exponentialBackoffDelay(attempt - 1, options.delayMultiplierMs),
        remainingMs,
      );
      const context: RetryWithBackoffErrorContext = {
        attempt,
        elapsedMs,
        nextDelayMs,
      };

      if (options.shouldRetry) {
        const retry = await options.shouldRetry(error, context);
        if (!retry) {
          throw error;
        }
      }

      if (remainingMs === 0) {
        break;
      }

      await options.onRetry?.(error, context);
      await new Promise(resolve => setTimeout(resolve, nextDelayMs));
    }
  }

  throw lastError;
}
